import type { ReactNode } from 'react';
import { Card } from '@/components/ui/card';
import { cn } from '@/lib/utils';

interface CreditMeterCardProps {
  icon: ReactNode;
  title: string;
  value: ReactNode;
  // How full the bar is, 0–100. Null hides the bar: there is no meaningful share to show (a plan
  // with no allowance, no top-ups bought).
  percent?: number | null;
  caption?: ReactNode;
  children?: ReactNode;
}

// One credit figure on the dashboard — plan credits, top-up credits, consumption — all drawn the
// same way so the three read as one wallet.
export default function CreditMeterCard({ icon, title, value, percent = null, caption, children }: CreditMeterCardProps) {
  return (
    <Card className="p-6 space-y-3">
      <div className="flex items-center gap-2 text-muted-foreground">
        {icon}
        <span className="text-sm font-medium">{title}</span>
      </div>
      <p className="text-2xl font-bold text-foreground tabular-nums">{value}</p>
      {percent != null && (
        <div
          className="h-2 w-full overflow-hidden rounded-full bg-muted"
          role="progressbar"
          aria-valuenow={percent}
          aria-valuemin={0}
          aria-valuemax={100}
        >
          <div
            className={cn('h-full rounded-full transition-all', percent >= 90 ? 'bg-destructive' : 'bg-primary')}
            style={{ width: `${Math.min(100, Math.max(0, percent))}%` }}
          />
        </div>
      )}
      {caption && <p className="text-xs text-muted-foreground">{caption}</p>}
      {children}
    </Card>
  );
}
